import { Fragment, useCallback, useEffect, useState } from 'react'
import {
  LuDownload,
  LuSearch,
  LuChevronDown,
  LuChevronRight,
  LuFileSpreadsheet,
  LuPencil,
  LuTrash2,
  LuX,
} from 'react-icons/lu'
import { toast } from 'react-toastify'
import { useLocation } from 'wouter'
import PropTypes from 'prop-types'

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000'

const ESTAT_BADGE = {
  pendent:  'bg-amber-100 text-amber-700',
  revisat:  'bg-blue-100 text-blue-700',
  exportat: 'bg-emerald-100 text-emerald-700',
}

const eur = (v) =>
  v == null || v === ''
    ? ''
    : Number(v).toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'

const fmtData = (v) => (v ? new Date(v).toLocaleDateString('ca-ES') : '')

// ─── Modal d'edició d'un contracte ─────────────────────────────────────────────
const isoToInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : '')

// Camps editables: [clau BD, etiqueta, tipus]
const EDIT_FIELDS = [
  ['nom_tercer', 'Proveïdor', 'text'],
  ['nif_tercer', 'NIF', 'text'],
  ['expedient', 'Expedient', 'text'],
  ['tipus_desti', 'Tipus contracte', 'text'],
  ['descripcio', 'Descripció', 'textarea'],
  ['codi_cpv', 'CPV', 'text'],
  ['municipi', 'Codi INE municipi', 'text'],
  ['import_sense_iva', 'Import s/IVA', 'number'],
  ['import_amb_iva', 'Import c/IVA', 'number'],
  ['tipus_iva', 'Tipus IVA', 'number'],
  ['data_adjudicacio', 'Data adjudicació', 'date'],
  ['data_formalitzacio', 'Data formalització', 'date'],
]

function ContracteEditModal({ contracte, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const f = {}
    EDIT_FIELDS.forEach(([k, , t]) => { f[k] = t === 'date' ? isoToInput(contracte[k]) : (contracte[k] ?? '') })
    return f
  })
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setSaving(true)
    try {
      const res = await fetch(`${API}/contractes/menors/${contracte.id}`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(form),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Error desant')
      toast.success('Contracte actualitzat')
      onSaved()
      onClose()
    } catch (err) {
      toast.error('Error desant: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Editar contracte #{contracte.id}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><LuX size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {EDIT_FIELDS.map(([k, label, type]) => (
            <div key={k} className={type === 'textarea' ? 'md:col-span-2' : ''}>
              <label className="block text-xs text-gray-500 mb-1">{label}</label>
              {type === 'textarea' ? (
                <textarea
                  value={form[k]} rows={2}
                  onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#006F53]/30" />
              ) : (
                <input
                  type={type} value={form[k]} step={type === 'number' ? 'any' : undefined}
                  onChange={(e) => setForm((s) => ({ ...s, [k]: e.target.value }))}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#006F53]/30" />
              )}
            </div>
          ))}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel·lar</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-2 text-sm bg-[#006F53] text-white rounded-lg hover:bg-[#005a44] disabled:opacity-60">
            {saving ? 'Desant...' : 'Desar canvis'}
          </button>
        </div>
      </div>
    </div>
  )
}

ContracteEditModal.propTypes = {
  contracte: PropTypes.object.isRequired,
  onClose:   PropTypes.func.isRequired,
  onSaved:   PropTypes.func.isRequired,
}

// ─── Detall d'una tanda (files de contractes) ──────────────────────────────────
function UploadRows({ uploadId }) {
  const [rows, setRows]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    fetch(`${API}/contractes/uploads/${uploadId}`)
      .then((r) => r.json())
      .then((d) => setRows(d.contractes || []))
      .catch(() => toast.error('Error carregant els contractes de la tanda'))
      .finally(() => setLoading(false))
  }, [uploadId])

  useEffect(() => { load() }, [load])

  if (loading) return <div className="p-4 text-sm text-gray-500">Carregant contractes...</div>
  if (!rows || rows.length === 0) return <div className="p-4 text-sm text-gray-500">Sense contractes.</div>

  return (
    <div className="overflow-x-auto border-t border-gray-100 bg-gray-50/60">
      <table className="min-w-full text-xs">
        <thead className="text-gray-500">
          <tr className="text-left">
            <th className="px-3 py-2">Data</th>
            <th className="px-3 py-2">Expedient</th>
            <th className="px-3 py-2">Proveïdor (NIF)</th>
            <th className="px-3 py-2">Descripció</th>
            <th className="px-3 py-2">CPV</th>
            <th className="px-3 py-2 text-right">Import c/IVA</th>
            <th className="px-3 py-2 text-right">Accions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-t border-gray-100 hover:bg-white">
              <td className="px-3 py-1.5 whitespace-nowrap">{fmtData(c.data_origen)}</td>
              <td className="px-3 py-1.5 whitespace-nowrap">{c.expedient}</td>
              <td className="px-3 py-1.5">
                <div className="font-medium text-gray-700">{c.nom_tercer}</div>
                <div className="text-gray-400">{c.nif_tercer}</div>
              </td>
              <td className="px-3 py-1.5 max-w-md truncate" title={c.descripcio || c.text_lliure}>
                {c.descripcio || c.text_lliure}
              </td>
              <td className="px-3 py-1.5 whitespace-nowrap">{c.codi_cpv}</td>
              <td className="px-3 py-1.5 text-right whitespace-nowrap">{eur(c.import_amb_iva)}</td>
              <td className="px-3 py-1.5 text-right">
                <button onClick={() => setEditing(c)}
                  className="inline-flex items-center gap-1 text-[#006F53] hover:underline">
                  <LuPencil size={13} /> Editar
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editing && (
        <ContracteEditModal contracte={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  )
}

UploadRows.propTypes = {
  uploadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
}

// ─── Cerca global entre totes les tandes ───────────────────────────────────────
function GlobalSearch() {
  const [q, setQ]           = useState('')
  const [res, setRes]       = useState(null)
  const [loading, setLoad]  = useState(false)

  const run = useCallback(async () => {
    if (!q.trim()) return
    setLoad(true)
    try {
      const r = await fetch(`${API}/contractes/menors?q=${encodeURIComponent(q.trim())}&limit=100`)
      setRes(await r.json())
    } catch {
      toast.error('Error en la cerca')
    } finally {
      setLoad(false)
    }
  }, [q])

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && run()}
            placeholder="Cerca per proveïdor, NIF, expedient o descripció a totes les tandes..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006F53]/30"
          />
        </div>
        <button
          onClick={run}
          className="px-4 py-2 text-sm bg-[#006F53] text-white rounded-lg hover:bg-[#005a44] transition-colors">
          Cercar
        </button>
      </div>

      {loading && <p className="mt-3 text-sm text-gray-500">Cercant...</p>}

      {res && !loading && (
        <div className="mt-4">
          <p className="text-xs text-gray-500 mb-2">
            {res.total} resultat{res.total !== 1 ? 's' : ''}
            {res.total > res.rows.length ? ` (mostrant ${res.rows.length})` : ''}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="text-gray-500 text-left">
                <tr>
                  <th className="px-3 py-2">Trim.</th>
                  <th className="px-3 py-2">Data</th>
                  <th className="px-3 py-2">Proveïdor (NIF)</th>
                  <th className="px-3 py-2">Expedient</th>
                  <th className="px-3 py-2">CPV</th>
                  <th className="px-3 py-2 text-right">Import c/IVA</th>
                </tr>
              </thead>
              <tbody>
                {res.rows.map((c) => (
                  <tr key={c.id} className="border-t border-gray-100">
                    <td className="px-3 py-1.5 whitespace-nowrap">{c.upload_any}·T{c.trimestre}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{fmtData(c.data_origen)}</td>
                    <td className="px-3 py-1.5">
                      <div className="font-medium text-gray-700">{c.nom_tercer}</div>
                      <div className="text-gray-400">{c.nif_tercer}</div>
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{c.expedient}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{c.codi_cpv}</td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">{eur(c.import_amb_iva)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Pàgina principal ──────────────────────────────────────────────────────────
export default function HistoricPage() {
  const [uploads, setUploads] = useState([])
  const [loading, setLoading] = useState(true)
  const [openId, setOpenId]   = useState(null)
  const [, setLocation]       = useLocation()

  const loadUploads = useCallback(() => {
    setLoading(true)
    fetch(`${API}/contractes/uploads`)
      .then((r) => r.json())
      .then(setUploads)
      .catch(() => toast.error('Error carregant l\'històric'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { loadUploads() }, [loadUploads])

  const download = (u) => {
    const fname = `agregades_menors_${u.any_execucio}_T${u.trimestre}.xls`
    toast.info(`Descarregant "${fname}"...`)
    // El backend regenera el .xls des de la BD, el retorna com a attachment i marca la tanda exportada.
    window.open(`${API}/contractes/uploads/${u.id}/export`, '_blank')
    // Refresca l'estat (passarà a "exportat") després d'un moment.
    setTimeout(loadUploads, 1500)
  }

  const remove = async (u) => {
    if (!window.confirm(`Eliminar la tanda ${u.any_execucio}·T${u.trimestre} (${u.total_menors + u.total_majors} contractes)? Aquesta acció no es pot desfer.`)) return
    try {
      const res = await fetch(`${API}/contractes/uploads/${u.id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error((await res.json()).error || 'Error')
      toast.success('Tanda eliminada')
      loadUploads()
    } catch (err) {
      toast.error('Error eliminant: ' + err.message)
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <LuFileSpreadsheet size={28} className="text-[#006F53]" />
        <div>
          <h1 className="text-xl font-semibold text-gray-800">Històric de contractes</h1>
          <p className="text-sm text-gray-500">Consulta tandes anteriors i torna a generar-ne l&apos;Excel</p>
        </div>
      </div>

      <GlobalSearch />

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3">Període</th>
                <th className="px-4 py-3">Fitxer</th>
                <th className="px-4 py-3 text-center">Menors</th>
                <th className="px-4 py-3 text-center">Majors</th>
                <th className="px-4 py-3">Estat</th>
                <th className="px-4 py-3">Data pujada</th>
                <th className="px-4 py-3 text-right">Accions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Carregant...</td></tr>
              )}
              {!loading && uploads.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Encara no hi ha tandes pujades.</td></tr>
              )}
              {uploads.map((u) => (
                <Fragment key={u.id}>
                  <tr className="border-t border-gray-100 hover:bg-gray-50/60">
                    <td className="px-4 py-3">
                      <button onClick={() => setOpenId(openId === u.id ? null : u.id)} className="text-gray-400 hover:text-[#006F53]">
                        {openId === u.id ? <LuChevronDown size={18} /> : <LuChevronRight size={18} />}
                      </button>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-700 whitespace-nowrap">{u.any_execucio} · T{u.trimestre}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={u.nom_fitxer}>{u.nom_fitxer}</td>
                    <td className="px-4 py-3 text-center">{u.total_menors}</td>
                    <td className="px-4 py-3 text-center">{u.total_majors}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTAT_BADGE[u.estat] || 'bg-gray-100 text-gray-600'}`}>
                        {u.estat}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtData(u.data_pujada)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => setLocation(`/contractes/editar/${u.id}`)}
                        title="Obrir aquesta tanda a l'editor per modificar-la"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#006F53] text-[#006F53] rounded-lg hover:bg-[#eef8f3] transition-colors">
                        <LuPencil size={14} /> Editar
                      </button>
                      <button
                        onClick={() => download(u)}
                        title={`Descarrega agregades_menors_${u.any_execucio}_T${u.trimestre}.xls`}
                        className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#006F53] text-white rounded-lg hover:bg-[#005a44] transition-colors">
                        <LuDownload size={14} /> Descarregar Excel
                      </button>
                      <button
                        onClick={() => remove(u)}
                        title="Eliminar tanda"
                        className="ml-2 inline-flex items-center p-1.5 text-xs text-gray-400 hover:text-red-500 border border-gray-200 rounded-lg hover:border-red-200 hover:bg-red-50 transition-colors">
                        <LuTrash2 size={14} />
                      </button>
                    </td>
                  </tr>
                  {openId === u.id && (
                    <tr>
                      <td colSpan={8} className="p-0"><UploadRows uploadId={u.id} /></td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
